import { computed, inject, ref, Ref, watch } from "vue";
import { v4 as uuidv4 } from "uuid";
import { ApiError, Result } from "@/api/base";
import useDialog from "@/ui/composables/useDialog";
import { useApiClient } from "@/api/client";
import { FileSearchParams } from "@/types/fileSearch";
import { LaravelPaginatedCollection } from "@/types/collections";
import { provideFileBrowserActions } from "@/ui/components/FileBrowser/useFileBrowserActions";
import { useFolderTree } from "@/ui/components/FileBrowser/useFolderTree";
import {
	DateFilterRange,
	FileBrowserAction,
	FilterPatch,
	folderKey,
	isSharedWithMeNode,
	ResourceTreeCategory,
} from "@/ui/components/FileBrowser/types";
import {
	FileColumn,
	FileSortKey,
	allColumnKeys,
	eligibleColumns,
} from "@/ui/components/FileBrowser/columns";
import FileDTO = App.Data.FileDTO;
import ResourceTreeNodeDO = App.Data.ResourceTreeNodeDO;
import {
	SelectionRemoval,
	selectionRemoval,
} from "@/ui/components/FileBrowser/actionAvailability";
import { useColumnPreferences } from "@/ui/components/FileBrowser/useColumnPreferences";
import { useBrowserDragDrop } from "@/ui/components/FileBrowser/useBrowserDragDrop";
import { DragPayload } from "@/ui/components/FileBrowser/moveRules";
import useToast from "@/ui/composables/useToast";
import { t } from "@/plugins/i18n";

export type FileTableRow =
	| { kind: "folder"; folder: ResourceTreeNodeDO }
	| { kind: "file"; file: FileDTO };

export type FileScope = "folder" | "global";

/**
 * What a page request did, in the vocabulary `v-infinite-scroll` expects: it
 * keeps asking after "ok", stops after "empty", and offers a retry after
 * "error" rather than firing again at an endpoint that just failed.
 */
export type PageLoadStatus = "ok" | "empty" | "error";

/**
 * Row identity, used both for selection membership and as the render key. The
 * kind has to be part of it: a folder and a file can carry the same id.
 */
export const rowKey = (row: FileTableRow): string =>
	row.kind === "folder" ? `folder-${row.folder.id}` : `file-${row.file.id}`;

export const isFileRow = (
	row: FileTableRow
): row is Extract<FileTableRow, { kind: "file" }> => row.kind === "file";

export const isFolderRow = (
	row: FileTableRow
): row is Extract<FileTableRow, { kind: "folder" }> => row.kind === "folder";

type ApiResult<T> = Promise<Result<T, ApiError>>;

export const useFileTable = (options: {
	resourceTreeCategory: Ref<ResourceTreeCategory>;
	actions: Ref<FileBrowserAction[]>;
}) => {
	const { resourceTreeCategory, actions } = options;

	provideFileBrowserActions(actions);

	const hasAction = (action: FileBrowserAction): boolean =>
		actions.value.includes(action);

	const openFileAction =
		inject<(file: FileDTO) => Promise<unknown>>("openFileAction");
	const removeFileAction =
		inject<(file: FileDTO) => ApiResult<unknown>>("removeFileAction");
	const batchRemoveFilesAction = inject<
		(files: FileDTO[]) => ApiResult<unknown>
	>("batchRemoveFilesAction");
	const closeAction = inject<() => Promise<unknown>>("closeAction");
	const updateDefinitionAction = inject<(file: FileDTO) => Promise<unknown>>(
		"updateDefinitionAction"
	);
	const updateFilePropertiesAction = inject<
		(file: FileDTO) => ApiResult<unknown>
	>("updateFilePropertiesAction");
	const toggleIsFavoriteAction = inject<(file: FileDTO) => ApiResult<unknown>>(
		"toggleIsFavoriteAction"
	);

	/* ---------------------------------------------------------------- *
	 * Folder tree
	 * ---------------------------------------------------------------- */

	const {
		folders,
		isLoadingFolders,
		nestedFolders,
		rootFolder,
		childFoldersOf,
		pathTo,
		pathLabelFor,
		getFolders,
		whenFoldersLoaded,
		folderToEdit,
		isManageFolderDialogVisible,
		createNewFolder: createFolderUnder,
		editFolder,
		removeFolder: removeFolderFromTree,
	} = useFolderTree({ resourceTreeCategory });

	/**
	 * Open tree nodes, keyed by folderKey rather than id - the synthetic
	 * "Shared with me" node has no id.
	 */
	const expandedFolderKeys: Ref<string[]> = ref([]);

	const toggleFolderExpanded = (key: string): void => {
		expandedFolderKeys.value = expandedFolderKeys.value.includes(key)
			? expandedFolderKeys.value.filter((expanded) => expanded !== key)
			: [...expandedFolderKeys.value, key];
	};

	/** The folder chain shown in the Location column while searching globally. */
	const locationOf = (file: FileDTO): string =>
		pathLabelFor(file.resource_tree_node_id) || "—";

	/* ---------------------------------------------------------------- *
	 * Navigation
	 * ---------------------------------------------------------------- */

	/** The folder chain the user has drilled into; drives the breadcrumb. */
	const path: Ref<ResourceTreeNodeDO[]> = ref([]);
	const selectedFolder = computed<ResourceTreeNodeDO | null>(
		() => path.value[path.value.length - 1] ?? null
	);

	/**
	 * TRAP 3 - clear the selection here, never in a watcher on the folder.
	 * Selecting a row can also change the folder, and a watcher fires afterwards
	 * and would wipe the selection that was just made.
	 */
	const openFolder = (folder: ResourceTreeNodeDO): void => {
		// Opening a folder from global results drops you inside it. Filters stay
		// on, now narrowed to that folder.
		scope.value = "folder";
		path.value = pathTo(folder);
		clearSelection();
		reload();
	};

	/**
	 * Re-resolves the breadcrumb against a freshly fetched tree, truncating at
	 * the first folder that is gone. Without the re-resolve a rename leaves a
	 * stale name in the breadcrumb; without the truncate, deleting a folder you
	 * are inside leaves you pointing at nothing.
	 */
	const resyncPath = (): void => {
		const resolved: ResourceTreeNodeDO[] = [];

		for (const node of path.value) {
			const fresh = isSharedWithMeNode(node)
				? node
				: folders.value.find((folder) => folder.id === node.id);

			if (!fresh) {
				break;
			}

			resolved.push(fresh);
		}

		const wasTruncated = resolved.length !== path.value.length;

		path.value = resolved.length
			? resolved
			: rootFolder.value
			? [rootFolder.value]
			: [];

		if (wasTruncated) {
			clearSelection();
			reload();
		}
	};

	/* ---------------------------------------------------------------- *
	 * Server query
	 * ---------------------------------------------------------------- */

	const scope: Ref<FileScope> = ref("folder");
	const search = ref("");
	const modifiedFrom: Ref<string | null> = ref(null);
	const modifiedTo: Ref<string | null> = ref(null);
	const modifiedByUserId: Ref<number | null> = ref(null);
	const createdFrom: Ref<string | null> = ref(null);
	const createdTo: Ref<string | null> = ref(null);
	const tagIds: Ref<number[]> = ref([]);
	const lastOpenedFrom: Ref<string | null> = ref(null);
	const lastOpenedTo: Ref<string | null> = ref(null);

	const dateFilters: Record<
		string,
		{ from: Ref<string | null>; to: Ref<string | null> }
	> = {
		created_at: { from: createdFrom, to: createdTo },
		last_modified_at: { from: modifiedFrom, to: modifiedTo },
		last_opened_at: { from: lastOpenedFrom, to: lastOpenedTo },
	};

	/** Whether a column is currently narrowing the query. */
	const isColumnFiltered = (key: string): boolean => {
		const range = dateFilters[key];

		if (range) {
			return !!range.from.value || !!range.to.value;
		}

		if (key === "last_modified_by") {
			return modifiedByUserId.value !== null;
		}

		return key === "tags" && tagIds.value.length > 0;
	};

	/** Drops whatever that column's menu had set. */
	const clearColumnFilter = (key: string): void => {
		const range = dateFilters[key];

		if (range) {
			range.from.value = null;
			range.to.value = null;

			return;
		}

		if (key === "last_modified_by") {
			modifiedByUserId.value = null;
		}

		if (key === "tags") {
			tagIds.value = [];
		}
	};

	/** The same ranges as plain values, for the table to render its menus from. */
	const dateFilterValues = computed<Record<string, DateFilterRange>>(() =>
		Object.fromEntries(
			Object.entries(dateFilters).map(([key, range]) => [
				key,
				{ from: range.from.value, to: range.to.value },
			])
		)
	);
	const sortKey: Ref<FileSortKey> = ref("title");
	const sortDescending = ref(false);

	const isSearchingEverywhere = computed(() => scope.value === "global");

	/** Pages are appended, not replaced - the table scrolls infinitely. */
	const pages: Ref<FileDTO[][]> = ref([]);
	const files = computed(() => pages.value.flat());
	const total = ref(0);
	const currentPage = ref(0);
	const lastPage = ref(1);
	const isLoadingPage = ref(false);

	const hasMorePages = computed(() => currentPage.value < lastPage.value);

	/** Maps the current UI state onto the endpoint's query parameters. */
	const searchParams = computed<FileSearchParams>(() => ({
		page: currentPage.value + 1,
		per_page: 50,
		sort: (sortDescending.value ? "-" : "") + sortKey.value,
		...(search.value ? { "filter[search]": search.value } : {}),
		// Omitting both folder filters is what makes the search global. The
		// shared bucket has no node id of its own - it is a scope on the same
		// endpoint, so sorting and the column filters keep working inside it.
		...(scope.value !== "folder"
			? {}
			: isSharedWithMeNode(selectedFolder.value)
			? { "filter[shared_with_me]": true }
			: selectedFolder.value?.id
			? { "filter[resource_tree_node_id]": selectedFolder.value.id }
			: {}),
		...(modifiedFrom.value
			? { "filter[modified_from]": modifiedFrom.value }
			: {}),
		...(modifiedTo.value ? { "filter[modified_to]": modifiedTo.value } : {}),
		...(modifiedByUserId.value
			? { "filter[modified_by_user_id]": modifiedByUserId.value }
			: {}),
		...(createdFrom.value ? { "filter[created_from]": createdFrom.value } : {}),
		...(createdTo.value ? { "filter[created_to]": createdTo.value } : {}),
		...(lastOpenedFrom.value
			? { "filter[last_opened_from]": lastOpenedFrom.value }
			: {}),
		...(lastOpenedTo.value
			? { "filter[last_opened_to]": lastOpenedTo.value }
			: {}),
		...(tagIds.value.length
			? { "filter[tag_ids]": tagIds.value.join(",") }
			: {}),
	}));

	const hasActiveFilters = computed(
		() =>
			!!search.value ||
			!!modifiedFrom.value ||
			!!modifiedTo.value ||
			!!modifiedByUserId.value ||
			!!createdFrom.value ||
			!!createdTo.value ||
			!!lastOpenedFrom.value ||
			!!lastOpenedTo.value ||
			tagIds.value.length > 0
	);

	/**
	 * Tracks the previous filter state so the scope only flips on the transition
	 * into or out of filtering. Without that, an explicit "This folder" choice
	 * would be undone by the next keystroke in the search box.
	 */
	let hadActiveFilters = false;

	/**
	 * Call after mutating any filter ref. Debouncing is the caller's job: the
	 * search box must not fire a request per keystroke.
	 *
	 * The scope decision is made synchronously rather than in a watcher, because
	 * a watcher would still be pending when reload() builds `searchParams` and
	 * the first request would carry the old scope.
	 */
	const applyFilters = async (): Promise<void> => {
		const isFiltering = hasActiveFilters.value;

		if (isFiltering !== hadActiveFilters) {
			scope.value = isFiltering ? "global" : "folder";
			hadActiveFilters = isFiltering;
		}

		await reload();
	};

	/** Routes one menu's patch to the column it came from, then requeries. */
	const applyColumnFilter = async (
		key: string,
		patch: FilterPatch
	): Promise<void> => {
		const range = dateFilters[key];

		if (range) {
			if ("from" in patch) range.from.value = patch.from ?? null;
			if ("to" in patch) range.to.value = patch.to ?? null;
		}

		if ("userId" in patch) {
			modifiedByUserId.value = patch.userId ?? null;
		}

		if ("tagIds" in patch) {
			tagIds.value = patch.tagIds ?? [];
		}

		await applyFilters();
	};

	/** An explicit scope choice, which outlives later filter edits. */
	const setScope = async (next: FileScope): Promise<void> => {
		scope.value = next;
		await reload();
	};

	const clearFilters = async (): Promise<void> => {
		search.value = "";
		modifiedFrom.value = null;
		modifiedTo.value = null;
		modifiedByUserId.value = null;
		createdFrom.value = null;
		createdTo.value = null;
		lastOpenedFrom.value = null;
		lastOpenedTo.value = null;
		tagIds.value = [];

		await applyFilters();
	};

	const fetchPage = (): ApiResult<LaravelPaginatedCollection<FileDTO>> =>
		resourceTreeCategory.value === "dashboards"
			? useApiClient("dashboard").searchDashboards(searchParams.value)
			: useApiClient("documents").searchStoredDocuments(searchParams.value);

	/**
	 * Bumped by every reload so a page that was already in flight cannot append
	 * itself on top of a newer result - which is exactly what a debounced search
	 * box would otherwise cause.
	 */
	let requestToken = 0;

	/**
	 * What the last request did, so the table can tell "this folder is empty"
	 * apart from "the listing failed" - the initial load included, which the
	 * scroller never sees.
	 */
	const lastLoadStatus: Ref<PageLoadStatus | null> = ref(null);

	const appendNextPage = async (): Promise<PageLoadStatus> => {
		const token = ++requestToken;
		isLoadingPage.value = true;

		const response = await fetchPage();

		if (token !== requestToken) {
			// A reload superseded this request; its rows belong to a stale query,
			// and the reload owns `isLoadingPage` from here on.
			return "ok";
		}

		isLoadingPage.value = false;

		if (!response.isOk()) {
			// Silence here reads as an empty folder, which is the one thing a failed
			// listing must not look like.
			useToast().error(t("fileBrowser.loadFailed"), { timeout: 2000 });
			lastLoadStatus.value = "error";

			return "error";
		}

		pages.value = [...pages.value, response.value.data];
		total.value = response.value.total;
		currentPage.value = response.value.current_page;
		lastPage.value = response.value.last_page;
		lastLoadStatus.value = hasMorePages.value ? "ok" : "empty";

		return lastLoadStatus.value;
	};

	/**
	 * The page in flight, so concurrent callers await one request rather than
	 * racing another onto the end of the list. The scroller is one of them: it
	 * asks again as soon as the sentinel is visible, which it still is while the
	 * first page is loading.
	 */
	let pendingLoad: Promise<PageLoadStatus> | null = null;

	/** Appends the next page, or reports why it did not. */
	const loadNextPage = (): Promise<PageLoadStatus> => {
		if (!hasMorePages.value) {
			return Promise.resolve<PageLoadStatus>("empty");
		}

		if (pendingLoad) {
			return pendingLoad;
		}

		const load = appendNextPage().finally(() => {
			// Only if it is still the current one: a reload clears it deliberately.
			if (pendingLoad === load) {
				pendingLoad = null;
			}
		});

		pendingLoad = load;

		return load;
	};

	/** Any filter or sort change starts again from page one. */
	const reload = async (): Promise<void> => {
		requestToken++;
		// Whatever is in flight is now stale, and must not be handed to the next
		// caller as if it were this query's first page.
		pendingLoad = null;
		lastLoadStatus.value = null;
		pages.value = [];
		total.value = 0;
		currentPage.value = 0;
		lastPage.value = 1;
		isLoadingPage.value = false;

		await loadNextPage();
	};

	/* ---------------------------------------------------------------- *
	 * Columns
	 * ---------------------------------------------------------------- */

	const {
		isColumnVisible,
		isColumnLocked,
		toggleColumn,
		resetColumnPreferences,
	} = useColumnPreferences(resourceTreeCategory.value);

	const chooserColumns = computed<FileColumn[]>(() =>
		eligibleColumns(resourceTreeCategory.value, isSearchingEverywhere.value)
	);

	const columns = computed<FileColumn[]>(() =>
		chooserColumns.value.filter((column) => isColumnVisible(column.key))
	);

	/**
	 * Everything the query names has to be on screen. A hidden column's filter is
	 * one the user cannot see, reach or clear, and a hidden column's sort is one
	 * they cannot read - so both fall away with the column, in a single requery.
	 */
	watch(columns, (renderedColumns) => {
		const renderedKeys = renderedColumns.map((column) => column.key);
		let hasChanged = false;

		for (const key of allColumnKeys()) {
			if (renderedKeys.includes(key) || !isColumnFiltered(key)) {
				continue;
			}

			clearColumnFilter(key);
			hasChanged = true;
		}

		if (!renderedColumns.some((column) => column.sortKey === sortKey.value)) {
			sortKey.value = "title";
			sortDescending.value = false;
			hasChanged = true;
		}

		if (!hasChanged) {
			return;
		}

		// Not applyFilters(): that would flip the scope, which would change the
		// eligible columns and re-enter this watcher. Keeping the flag in step is
		// what lets the next real filter edit flip the scope as it should.
		hadActiveFilters = hasActiveFilters.value;
		reload();
	});

	/* ---------------------------------------------------------------- *
	 * Rows
	 * ---------------------------------------------------------------- */

	/**
	 * Folders first, then the server's files. Because folders never enter the
	 * query, they stay pinned on top whichever way the sort runs - no comparator
	 * trickery, and nothing for a descending sort to invert.
	 */
	const rows = computed<FileTableRow[]>(() => [
		...(scope.value === "folder"
			? childFoldersOf(selectedFolder.value).map(
					(folder): FileTableRow => ({ kind: "folder", folder })
			  )
			: []),
		...files.value.map((file): FileTableRow => ({ kind: "file", file })),
	]);

	/**
	 * What the table is showing, which is not `total`: that is the server's count
	 * of matching files, and folders never enter the query. Counting the folder
	 * rows themselves is exact - they are all present, never paginated.
	 */
	const totalItems = computed(
		() => total.value + rows.value.filter(isFolderRow).length
	);

	const setSort = (key: FileSortKey): void => {
		if (sortKey.value === key) {
			if (sortDescending.value && key !== "title") {
				sortKey.value = "title";
				sortDescending.value = false;
			} else {
				sortDescending.value = !sortDescending.value;
			}
		} else {
			sortKey.value = key;
			sortDescending.value = false;
		}

		reload();
	};

	/* ---------------------------------------------------------------- *
	 * Selection
	 * ---------------------------------------------------------------- */

	/**
	 * Rows, not files: folders are selectable too, so a shift-click range can
	 * hold both kinds. Callers that only act on one kind read `selectedFiles` or
	 * `selectedFolders`.
	 */
	const selection: Ref<FileTableRow[]> = ref([]);
	const anchorIndex: Ref<number | null> = ref(null);

	const selectedFiles = computed<FileDTO[]>(() =>
		selection.value.filter(isFileRow).map((row) => row.file)
	);

	const selectedFolders = computed<ResourceTreeNodeDO[]>(() =>
		selection.value.filter(isFolderRow).map((row) => row.folder)
	);

	/**
	 * TRAP 4 - this is a computed, so it cannot be assigned to.
	 * createNewFile() needs somewhere to put a blank file before opening the
	 * dialog: that is what fileBeingEdited is for.
	 *
	 * Null unless the selection is exactly one file. Every single-file action
	 * hangs off this, and a selected folder is not one of them.
	 */
	const selectedFile = computed<FileDTO | null>(() =>
		selection.value.length === 1 ? selectedFiles.value[0] ?? null : null
	);
	const fileBeingEdited: Ref<FileDTO | null> = ref(null);

	/**
	 * The deletable part of the selection and the confirm it needs. The footer
	 * button, the confirm dialog and removeSelection all read this, so they
	 * cannot disagree about what a delete would touch.
	 */
	const removal = computed<SelectionRemoval>(() =>
		selectionRemoval(selectedFiles.value, selectedFolders.value, hasAction)
	);

	const canRemoveSelection = computed<boolean>(
		() => removal.value.confirm !== "none"
	);

	const isRowSelected = (row: FileTableRow): boolean =>
		selection.value.some((selected) => rowKey(selected) === rowKey(row));

	const clearSelection = (): void => {
		selection.value = [];
		anchorIndex.value = null;
	};

	/** Plain click replaces, ctrl/cmd toggles, shift extends from the anchor. */
	const selectRow = (
		index: number,
		modifiers: { ctrl: boolean; shift: boolean }
	): void => {
		const row = rows.value[index];

		if (!row) {
			return;
		}

		if (modifiers.shift && anchorIndex.value !== null) {
			const [from, to] = [anchorIndex.value, index].sort((a, b) => a - b);

			selection.value = rows.value.slice(from, to + 1);

			return;
		}

		anchorIndex.value = index;

		if (modifiers.ctrl) {
			const key = rowKey(row);

			selection.value = isRowSelected(row)
				? selection.value.filter((selected) => rowKey(selected) !== key)
				: [...selection.value, row];

			return;
		}

		selection.value = [row];
	};

	/**
	 * Refresh after a save. The rows in hand still hold what the server had
	 * before the edit, and `selection` holds those same objects - so the
	 * properties panel, which renders the selected one, would keep showing the
	 * old title and tags. Reloading replaces the rows; re-pointing the selection
	 * by key makes the panel follow.
	 */
	const reloadKeepingSelection = async (): Promise<void> => {
		const selectedKeys = selection.value.map(rowKey);

		await reload();

		if (!selectedKeys.length) {
			return;
		}

		const refreshed = rows.value.filter((row) =>
			selectedKeys.includes(rowKey(row))
		);

		// A saved file can drop out of the active filter or off page one. Keeping
		// the stale object beats emptying the panel underneath the user, so the
		// selection only moves when every row was found again.
		if (refreshed.length === selectedKeys.length) {
			selection.value = refreshed;
		}
	};

	/* ---------------------------------------------------------------- *
	 * Dialogs and mutations
	 * ---------------------------------------------------------------- */

	const isManageFileDialogVisible = ref(false);
	const isShareResourceDialogVisible = ref(false);
	const isBatchDeleteDialogVisible = ref(false);

	/** Where a newly saved file lands: next to the selection, else the folder. */
	const resolveTargetFolderId = async (): Promise<number | undefined> => {
		await whenFoldersLoaded();

		return (
			selectedFile.value?.resource_tree_node_id ??
			selectedFolder.value?.id ??
			rootFolder.value?.id
		);
	};

	const createNewFile = async (): Promise<void> => {
		const resourceTreeNodeId = await resolveTargetFolderId();

		// Assigned to fileBeingEdited, not selectedFile - the latter is a computed
		// over the selection and cannot be written to.
		fileBeingEdited.value = {
			uuid: uuidv4(),
			resource_tree_node_id: resourceTreeNodeId ?? null,
			title: "",
			description: "",
			times_opened: 0,
			is_favorite: false,
			last_opened_at: null,
			last_modified_at: null,
			created_at: null,
			// Dashboards carry a mode, and the dialog asks for it rather than a
			// second dialog after save. Web is what the backend defaults to.
			...(resourceTreeCategory.value === "dashboards"
				? { mode: "web" as const }
				: {}),
		};
		isManageFileDialogVisible.value = true;
	};

	const editSelectedFile = () => {
		if (!selectedFile.value) return;

		fileBeingEdited.value = { ...selectedFile.value };
		isManageFileDialogVisible.value = true;
	};

	/** New folders nest under whichever folder is open. */
	const createNewFolder = (): Promise<void> =>
		createFolderUnder(selectedFolder.value?.id);

	const removeFolder = async (node: ResourceTreeNodeDO): Promise<void> => {
		const wasRemoved = await removeFolderFromTree(node);

		resyncPath();

		if (wasRemoved) {
			// What the folder held has moved up to Root, so the rows change too.
			await reload();
		}
	};

	/**
	 * One request per folder - there is no batch endpoint. Failures are
	 * collected rather than toasted one by one, and the tree is re-read once at
	 * the end. No confirm of its own: the caller has already asked.
	 */
	const removeFolders = async (
		foldersToRemove: ResourceTreeNodeDO[]
	): Promise<void> => {
		const failed: string[] = [];

		for (const folder of foldersToRemove) {
			const response = await useApiClient(
				"resourceTreeNodes"
			).removeResourceTreeNode(folder);

			if (!response.isOk()) {
				failed.push(folder.name);
			}
		}

		if (failed.length) {
			useToast().error(
				failed.length === foldersToRemove.length
					? t("fileBrowser.delete.foldersFailed")
					: t("fileBrowser.delete.someFoldersFailed", {
							names: failed.join(", "),
					  })
			);
		}

		await getFolders();
		resyncPath();
	};

	let batchDeleteResolver: ((confirmed: boolean) => void) | null = null;

	/** Bridges BatchDeleteDialog's confirm/cancel events back into a promise. */
	const confirmBatchDelete = (): Promise<boolean> =>
		new Promise<boolean>((resolve) => {
			batchDeleteResolver = resolve;
			isBatchDeleteDialogVisible.value = true;
		});

	const resolveBatchDelete = (confirmed: boolean): void => {
		batchDeleteResolver?.(confirmed);
		batchDeleteResolver = null;
	};

	/**
	 * A single row confirms inline; anything more goes through the impact-table
	 * dialog, which spells out what each half of the selection takes with it.
	 *
	 * Files go in one batch request, folders one at a time, and the listing is
	 * reloaded once at the end - deleting a folder moves what it held up to
	 * Root, so rows can arrive as well as leave.
	 */
	const removeSelection = async (): Promise<void> => {
		// `files` is named apart from the computed, which is every loaded file.
		const {
			files: filesToRemove,
			folders: foldersToRemove,
			confirm,
		} = removal.value;

		if (confirm === "none") {
			return;
		}

		// A folder on its own keeps the confirm the tree uses, which names it.
		if (confirm === "folder") {
			await removeFolder(foldersToRemove[0]);
			clearSelection();

			return;
		}

		if (confirm === "file") {
			const file = filesToRemove[0];
			// Absent rather than zero on a dashboard listing, so it has to be
			// resolved to a number before it can be compared as one.
			const usedOnDashboards = file.dashboard_cards_count ?? 0;
			const message =
				usedOnDashboards > 0
					? t("fileBrowser.delete.fileInUseMessage", {
							count: usedOnDashboards,
					  })
					: t("fileBrowser.delete.fileMessage", { title: file.title });

			const confirmed = await useDialog().confirm({
				title: t("fileBrowser.delete.fileTitle"),
				message,
				isDelete: true,
			});

			if (!confirmed) return;

			const response = await removeFileAction(file);

			if (response.isOk()) {
				clearSelection();
				await reload();
			}

			return;
		}

		if (!(await confirmBatchDelete())) return;

		// A failed half toasts on its own; the other half still goes, because
		// the user confirmed the whole selection.
		if (filesToRemove.length) {
			await batchRemoveFilesAction(filesToRemove);
		}

		if (foldersToRemove.length) {
			await removeFolders(foldersToRemove);
		}

		clearSelection();
		await reload();
	};

	const openSelectedFile = async (): Promise<void> => {
		if (selectedFile.value) {
			await openFileAction(selectedFile.value);
		}
	};

	const updateSelectedFileDefinition = async (): Promise<void> => {
		if (selectedFile.value) {
			await updateDefinitionAction(selectedFile.value);
		}
	};

	const toggleSelectedFileFavorite = async (): Promise<void> => {
		const file = selectedFile.value;

		if (!file) {
			return;
		}

		file.is_favorite = !file.is_favorite;

		const response = await toggleIsFavoriteAction(file);

		if (!response.isOk()) {
			file.is_favorite = !file.is_favorite;
			useToast().error(t("fileBrowser.favoriteFailed"));

			return;
		}

		await reloadKeepingSelection();
	};

	/* ---------------------------------------------------------------- *
	 * Moving by drag and drop
	 * ---------------------------------------------------------------- */

	/**
	 * Moves whatever a drag was carrying into one folder.
	 *
	 * A file's folder is just a field on it and a folder's parent is just a
	 * field on it, so both are ordinary property updates - one request each,
	 * because there is no batch endpoint for either. A partial failure has to
	 * name what stayed behind, so the responses are collected rather than
	 * toasted one by one.
	 */
	const moveToFolder = async (
		payload: DragPayload,
		folderId: number
	): Promise<void> => {
		const movingFiles = payload.files.filter(
			(file) => file.resource_tree_node_id !== folderId
		);
		const movingFolders = payload.folders.filter(
			(folder) => folder.parent_id !== folderId
		);

		if (!movingFiles.length && !movingFolders.length) {
			return;
		}

		const failed: string[] = [];

		for (const file of movingFiles) {
			const response = await updateFilePropertiesAction({
				...file,
				resource_tree_node_id: folderId,
			});

			if (!response.isOk()) {
				failed.push(file.title);
			}
		}

		for (const folder of movingFolders) {
			const response = await useApiClient(
				"resourceTreeNodes"
			).updateResourceTreeNode({
				...folder,
				parent_id: folderId,
			});

			if (!response.isOk()) {
				failed.push(folder.name);
			}
		}

		if (failed.length) {
			useToast().error(
				failed.length === movingFiles.length + movingFolders.length
					? t("fileBrowser.move.failed")
					: t("fileBrowser.move.someFailed", { names: failed.join(", ") })
			);
		}

		// The moved rows hold their old parent, and whatever moved has left the
		// folder being viewed anyway.
		clearSelection();

		if (movingFolders.length) {
			await getFolders();
			resyncPath();
		}

		await reload();
	};

	const dragDrop = useBrowserDragDrop({ folders, move: moveToFolder });

	const close = async (): Promise<void> => {
		await closeAction();
	};

	// First tree fetch drops the user in Root; later fetches only resync.
	watch(folders, () => {
		if (!path.value.length && rootFolder.value) {
			path.value = [rootFolder.value];
			reload();

			return;
		}

		resyncPath();
	});

	// Keep the tree open down to wherever the user is, however they got there.
	watch(path, () => {
		expandedFolderKeys.value = [
			...new Set([...expandedFolderKeys.value, ...path.value.map(folderKey)]),
		];
	});

	return {
		// tree
		folders,
		nestedFolders,
		rootFolder,
		childFoldersOf,
		expandedFolderKeys,
		toggleFolderExpanded,
		locationOf,
		isLoadingFolders,
		getFolders,
		editFolder,
		// navigation
		path,
		selectedFolder,
		openFolder,
		// query
		scope,
		search,
		modifiedFrom,
		modifiedTo,
		modifiedByUserId,
		createdFrom,
		createdTo,
		lastOpenedFrom,
		lastOpenedTo,
		dateFilterValues,
		tagIds,
		applyColumnFilter,
		sortKey,
		sortDescending,
		isSearchingEverywhere,
		hasActiveFilters,
		applyFilters,
		setScope,
		clearFilters,
		searchParams,
		total,
		totalItems,
		isLoadingPage,
		lastLoadStatus,
		hasMorePages,
		loadNextPage,
		reload,
		reloadKeepingSelection,
		setSort,
		// columns
		columns,
		chooserColumns,
		isColumnVisible,
		isColumnLocked,
		toggleColumn,
		resetColumnPreferences,
		// rows and selection
		rows,
		selection,
		anchorIndex,
		selectedFile,
		selectedFiles,
		selectedFolders,
		removal,
		canRemoveSelection,
		isRowSelected,
		fileBeingEdited,
		selectRow,
		clearSelection,
		// mutations
		resolveTargetFolderId,
		createNewFile,
		editSelectedFile,
		createNewFolder,
		removeFolder,
		removeFolders,
		removeSelection,
		resolveBatchDelete,
		openSelectedFile,
		updateSelectedFileDefinition,
		toggleSelectedFileFavorite,
		moveToFolder,
		dragDrop,
		close,
		// dialogs
		isManageFolderDialogVisible,
		isManageFileDialogVisible,
		isShareResourceDialogVisible,
		isBatchDeleteDialogVisible,
		folderToEdit,
		// actions
		hasAction,
	};
};
